import React, {
  useState, useRef, useEffect,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectResolverQueryParamByKey } from '../../services/resolver/selectors'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { parse as parseQuery, stringify } from '../../services/query-string/index'
import {
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_CONVERSATION_STARTED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { selectHasVoiceAccess } from '../../services/oracle/selectors'
import { selectAuth } from '../../services/auth/selectors'
import { selectUserLanguagePrimary } from '../../services/user/index'
import { OracleScrollIndicator } from '../OracleCommons/index'
import { usePostHogEvent } from '../PostHog/index'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import OracleIconButton from '../OracleCommons/OracleIconButton'
import OracleVoiceInput from './OracleVoiceInput'
import { OracleTooltip } from './OracleTooltip'
import { RateLimitState, getRateLimitWarningColor } from './rateLimitUtils'
import OracleRateLimitMessage from './OracleRateLimitMessage'
import { usePersonalizedSeedQuestionListQuery } from './queries.generated'

/**
 * Determines if voice input should be shown based on subscription access
 *
 * Uses Redux state to check if user has aiSpeech entitlement
 */
function shouldShowVoiceInput (options?: {
  hasVoiceAccess?: boolean
}): boolean {
  const hasVoiceAccess = options?.hasVoiceAccess ?? false
  return hasVoiceAccess
}

interface PillData {
  id: string
  question: string
}

interface OracleChatInputProps {
  sendChat?: (message: string) => void
  placeholder?: string
  className?: string
  appendText?: string
  isOperationInProgress?: boolean
  onStop?: () => void
  pendingMessage?: string
  onEnterVoiceMode?: () => void
  // Rate limiting props
  rateLimitState?: RateLimitState
  // Scroll indicator props
  showScrollIndicator?: boolean
  onScrollToBottom?: () => void
  showPills: boolean
  showDisclaimer: boolean
}

const OracleChatInput: React.FC<OracleChatInputProps> = ({
  sendChat,
  placeholder,
  className = '',
  appendText = '',
  isOperationInProgress = false,
  onStop,
  pendingMessage = '',
  onEnterVoiceMode,
  rateLimitState,
  showScrollIndicator = false,
  onScrollToBottom,
  showPills,
  showDisclaimer = true,
}) => {
  const [value, setValue] = useState('')
  const [storedMessage, setStoredMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const currentChatSessionId = useSelector(selectResolverQueryParamByKey('chatSessionId'))
  const dispatch = useDispatch()
  const trackMessageSent = usePostHogEvent('ai_message_sent')

  // hasVoiceAccess tells us if their account allows voice access or not
  const hasVoiceAccess = useSelector(selectHasVoiceAccess)

  // Check if voice is rate limited - if so, don't show voice input
  const isVoiceRateLimited = rateLimitState?.voiceRateLimitState?.rateLimited ?? false

  // showVoiceInput tells us if voice is allowed and should currently show the icon
  // Hide voice button when rate limited for voice
  const showVoiceInput = shouldShowVoiceInput({ hasVoiceAccess })
    && value.length === 0
    && !isVoiceRateLimited

  // Rate limiting UI state
  // Only show rate limit UI to users who are rate-limited (not textUnlimited)
  const isRateLimited = rateLimitState?.isRateLimitApplicable === true
    && rateLimitState.rateLimited === true

  // Trial countdown — only for unentitled (lifetime) users who aren't fully locked out yet
  const isTrialUser = rateLimitState?.limitType === 'lifetime'
  const shouldShowTrialCountdown = isTrialUser
    && rateLimitState?.isRateLimitApplicable === true
    && !rateLimitState.rateLimited
    && (rateLimitState.remainingInteractions ?? 0) > 0
  const trialWarningColor = shouldShowTrialCountdown
    ? getRateLimitWarningColor(rateLimitState?.remainingInteractions ?? 0) : 'orange'

  const defaultPlaceholder = useTranslatedText({ keys: ['chatInput', 'placeholder'] })
  const sendMessageAriaLabel = useTranslatedText({ keys: ['chatInput', 'sendMessageAriaLabel'] })
  const stopGenerationAriaLabel = useTranslatedText({ keys: ['chatInput', 'stopGenerationAriaLabel'] })
  const disclaimerText = useTranslatedText({ keys: ['chatInput', 'disclaimerText'] })
  const disclaimerLink = useTranslatedText({ keys: ['chatInput', 'disclaimerLink'] })
  const disclaimerTextEnd = useTranslatedText({ keys: ['chatInput', 'disclaimerTextEnd'] })
  const voiceConversationTooltip = useTranslatedText({ keys: ['chatInput', 'voiceConversationTooltip'] })
  const stopTooltip = useTranslatedText({ keys: ['chatInput', 'stopTooltip'] })
  const submitTooltip = useTranslatedText({ keys: ['chatInput', 'submitTooltip'] })
  const trialRemainingSingular = useTranslatedText({ keys: ['rateLimit', 'trialRemainingSingular'] })
  const trialRemainingPlural = useTranslatedText({
    keys: ['rateLimit', 'trialRemainingPlural'],
    params: { count: (rateLimitState?.remainingInteractions || 0).toString() },
  })

  // Get current query parameters to preserve them in the terms-privacy link
  const currentQuery = parseQuery(window.location.search)
  const termsPrivacyUrl = Object.keys(currentQuery).length > 0
    ? `https://www.gaia.com/terms-privacy?${stringify(currentQuery)}`
    : 'https://www.gaia.com/terms-privacy'

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  useEffect(() => {
    if (appendText) {
      setValue(() => {
        return appendText
      })
    }
  }, [appendText])

  // Handle pending message restoration when operation is cancelled
  useEffect(() => {
    if (pendingMessage && !isOperationInProgress && storedMessage) {
      setValue(pendingMessage)
      setStoredMessage('')
    }
  }, [pendingMessage, isOperationInProgress, storedMessage])

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current && !isRateLimited) {
      const textarea = textareaRef.current
      // For normal input: respect max-height and allow scrolling
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 400)}px`
    }
  }, [value, isRateLimited])

  const maybeSendChat = () => {
    // Check rate limiting before sending
    if (rateLimitState?.isRateLimitApplicable && rateLimitState.rateLimited) {
      // Don't send if user is rate limited
      return
    }

    if (value && sendChat) {
      // Store the message before sending for potential restoration
      setStoredMessage(value)
      sendChat(value)
      trackMessageSent(value, 'Text', 'Text Field')
      setValue('')
      // Reset textarea height after clearing
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
      // AI GA4 event - fires when message is actually sent
      dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
        ui_element: 'Text Field',
        input_method: 'text sent',
      })))
      // AI GA4 event
      if (!currentChatSessionId) {
        dispatch(setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
          ui_element: 'Text Field',
        })))
      }
    }
  }

  const handleStop = () => {
    if (onStop) {
      onStop()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      // If shiftKey is pressed, allow default behavior (new line)
      // Otherwise, send the message (but only if not currently streaming)
      if (!e.shiftKey && sendChat && !isOperationInProgress) {
        e.preventDefault()
        maybeSendChat()
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
  }

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget
    const { activeElement } = document

    // Only focus textarea if no element within the container already has focus
    if (!activeElement || !container.contains(activeElement)) {
      textareaRef.current?.focus()
    }
  }

  // Only one action icon is allowed: Voice, Send, or Stop.
  const makeAptIconButton = () => {
    // showVoiceInput already excludes rate-limited voice, so if it's true, voice is available
    if (!isOperationInProgress && showVoiceInput) {
      return <OracleTooltip title={voiceConversationTooltip}>
        <OracleVoiceInput
          onVoiceClick={onEnterVoiceMode}
          disabled={isOperationInProgress}
        />
        </OracleTooltip>
    }
    if (isOperationInProgress) {
      return <OracleTooltip title={stopTooltip}>
          <OracleIconButton
            icon="stop"
            invert={true}
            aria-label={stopGenerationAriaLabel}
            onClick={handleStop}
          />
      </OracleTooltip>
    }
    return <OracleTooltip title={submitTooltip}>
      <OracleIconButton
        icon="send-message"
        invert={true}
        aria-label={sendMessageAriaLabel}
        onClick={maybeSendChat}
      />
    </OracleTooltip>
  }

  return (
    <div className={`oracle-chat-input ${className}`} data-testid="chat-input">
      {/* Scroll to bottom indicator */}
      <OracleScrollIndicator
        isVisible={showScrollIndicator}
        onClick={onScrollToBottom}
      />
      <div
        className={`oracle-chat-input__container ${
          isRateLimited ? 'oracle-chat-input__container--rate-limited' : ''
        }`}
        onClick={isRateLimited ? undefined : handleContainerClick}
      >
        {isRateLimited ? (
          <OracleRateLimitMessage limitType={rateLimitState?.limitType} />
        ) : (
          <>
            <textarea
              id="oracle-text-input"
              ref={textareaRef}
              className="oracle-chat-input__textarea"
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || defaultPlaceholder}
              rows={1}
            />
            <div className={`oracle-chat-input__actions ${showPills ? 'oracle-chat-input__headroom' : ''}`}>
              {showPills && <InlineSeeds sendChat={sendChat} />}
              {makeAptIconButton()}
            </div>
            {shouldShowTrialCountdown && (
              <div
                className={`oracle-chat-input__rate-limit-warning ${
                  trialWarningColor === 'red'
                    ? 'oracle-chat-input__rate-limit-warning--critical'
                    : 'oracle-chat-input__rate-limit-warning--warning'
                }`}
              >
                {rateLimitState?.remainingInteractions === 1
                  ? trialRemainingSingular
                  : trialRemainingPlural}
              </div>
            )}
          </>
        )}
      </div>
      { showDisclaimer
        && <div className="oracle-chat-input__disclaimer">
          <span>{disclaimerText} <a
              href={termsPrivacyUrl}>{disclaimerLink}</a> {disclaimerTextEnd}
          </span>
        </div>
      }
    </div>
  )
}

interface InlineSeedsProps {
  sendChat?: (message: string) => void
}

const InlineSeeds: React.FC<InlineSeedsProps> = ({ sendChat }) => {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const language = useSelector(selectUserLanguagePrimary)
  // Fetch seed questions - over-fetching ok in case screen size changes
  const {
    data: seedData,
    error: seedError,
    loading: seedLoading,
  } = usePersonalizedSeedQuestionListQuery({
    variables: {
      limit: 4,
      language,
    },
    context: {
      jwt: auth.get('jwt'),
    },
  })

  // Get seed questions from GraphQL data
  const seedQuestions = seedData?.userInfoCurrent?.userInfo?.seedQuestions || []

  const handlePillClick = (pill: PillData) => {
    // If sendChat is provided, auto-send the question directly
    if (sendChat) {
      sendChat(pill.question)
    }

    // AI GA4 event
    dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: 'Prompt',
      input_method: 'prompt',
    })))
  }
  return (<>
          {/* Seed questions - only show if we have data and no errors */}
          {seedQuestions.length > 0 && !seedError && !seedLoading && (
            <div className="oracle-chat-input__seed-questions">
              {seedQuestions.map((pill) => (
                <button
                  key={pill.id}
                  type="button"
                  className="oracle-chat-input__seed-question"
                  onClick={() => handlePillClick(pill)}
                >
                  <div className="oracle-chat-input__seed-question-text">
                    {pill.question}
                  </div>
                </button>
              ))}
            </div>
          )}
          </>
  )
}

export default OracleChatInput
